import { ProcessingScreen } from '@components'

import { IconButton, Link, ModuleLayout, Space } from '@billy/design-system'
import { Icon, Tag, TagColor, TagSize, TagType, Typography, XStack, YStack } from '@shinetools/lumen-react'
import { colors } from '@shinetools/lumen-tokens/web/colors'
import { Backpack, ChevronRight } from 'lucide-react'
import { ReactElement, useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { useBackToSchoolOfferCheckout } from '../../hooks/useBackToSchoolOfferCheckout'
import { useTrackBackToSchoolCampaign } from '../../hooks/useTrackBackToSchoolCampaign'
import type { BackToSchoolAudience } from '../../types/backToSchoolAudience'
import type { BackToSchoolCheckoutParams } from '../../types/backToSchoolCheckoutParams'
import { getBackToSchoolOffers } from '../../utils/getBackToSchoolOffers'
import { getTimeOnPageInSeconds } from '../../utils/getTimeOnPageInSeconds'
import { isFreeAudience } from '../../utils/isFreeAudience'
import { BackToSchoolPlanCard } from '../BackToSchoolPlanCard'
import { ComingSoonSection } from '../ComingSoonSection'
import * as Styled from './styles'

interface BackToSchoolCampaignProps {
  audience: BackToSchoolAudience
  onEditSubscription: (params: BackToSchoolCheckoutParams) => void
  onClose?: () => void
  onViewMonthlyPlans?: () => void
  remainingCampaignDays: number | null
}

export const BackToSchoolCampaign = ({
  audience,
  onClose,
  onEditSubscription,
  onViewMonthlyPlans,
  remainingCampaignDays,
}: BackToSchoolCampaignProps): ReactElement => {
  const pageOpenedAtRef = useRef<number | null>(null)
  const { t } = useTranslation()
  const { isSubmitting, openCheckout } = useBackToSchoolOfferCheckout(onEditSubscription)
  const { trackCampaign } = useTrackBackToSchoolCampaign()

  const isFreePlanAudience = isFreeAudience(audience)

  const backToSchoolOffers = useMemo(() => getBackToSchoolOffers(), [])

  const { subtitle, title } = useMemo(() => {
    switch (audience) {
      case 'free':
        return { subtitle: t('back_to_school.subtitle.free'), title: t('back_to_school.title.free') }
      case 'expired_trial':
        return {
          subtitle: t('back_to_school.subtitle.expired_trial'),
          title: t('back_to_school.title.expired_trial'),
        }
      case 'trial':
        return { subtitle: t('back_to_school.subtitle.trial'), title: t('back_to_school.title.trial') }
      default:
        return { subtitle: '', title: '' }
    }
  }, [audience, t])

  const handleCloseClick = useCallback(() => {
    trackCampaign('Page Dismissed', {
      timeOnPageSeconds: getTimeOnPageInSeconds(pageOpenedAtRef.current),
    })
    onClose?.()
  }, [onClose, trackCampaign])

  const handleViewMonthlyPlansClick = useCallback(() => {
    trackCampaign('Monthly Plans Viewed')
    onViewMonthlyPlans?.()
  }, [onViewMonthlyPlans, trackCampaign])

  useEffect(() => {
    if (!audience || pageOpenedAtRef.current !== null) {
      return
    }
    pageOpenedAtRef.current = Date.now()
    trackCampaign('Page Viewed', {
      daysRemaining: remainingCampaignDays,
      plansDisplayed: backToSchoolOffers.map((offer) => offer.plan),
      targetSegment: audience,
    })
  }, [audience, backToSchoolOffers, remainingCampaignDays, trackCampaign])

  return (
    <>
      {isSubmitting && <ProcessingScreen />}
      <ModuleLayout fullHeight sideActions={onClose ? <IconButton icon="x" onClick={handleCloseClick} /> : undefined}>
        <Styled.BackToSchoolWrapper>
          <YStack align="center" gap={40}>
            <YStack align="center" gap={16}>
              <Tag
                color={TagColor.cyan}
                icon={<Backpack size={14} />}
                label={isFreePlanAudience ? t('back_to_school.badge.free') : t('back_to_school.badge.trial')}
                size={TagSize.medium}
                type={TagType.subtle}
              />
              <Typography.Header as="h1">{title}</Typography.Header>
              <Typography.Text color={colors.content.neutral.medium}>{subtitle}</Typography.Text>
            </YStack>
            <Styled.PlanCardsGrid>
              {backToSchoolOffers.map((offer) => (
                <BackToSchoolPlanCard
                  key={offer.plan}
                  offer={offer}
                  onSelect={(promoPriceAmount) => openCheckout(offer.plan, promoPriceAmount, offer.promoCode)}
                />
              ))}
            </Styled.PlanCardsGrid>
          </YStack>
          <Space size="s16" />
          {onViewMonthlyPlans && (
            <Link onClick={handleViewMonthlyPlansClick} underlined={false}>
              <XStack align="center">
                <Typography.Text weight="semibold">{t('back_to_school.see_offers')}</Typography.Text>
                <Icon icon={ChevronRight} size="small" />
              </XStack>
            </Link>
          )}
          <Space size="s32" />
          <ComingSoonSection />
          <Space size="s40" />
          <Typography.Text color={colors.content.neutral.medium} size="small">
            {t('back_to_school.legal')}
          </Typography.Text>
        </Styled.BackToSchoolWrapper>
        <Space size="s24" />
      </ModuleLayout>
    </>
  )
}
